import { getTriggerDateSort } from './trigger-date.js';
import { getSurveyOverdueStatus } from "./survey.js";
import { getContactWarning, getContactOverdueOnly, getContactSort } from "./contact.js";
import { concatenateStrings } from './string.js';
import { formatDate, formatDateTime, getDaysDifference, getIsoDateStringAsDate, isDateInPast } from './date-time.js';
import { getReferenceDataSiteName, getReferenceDataItemFromList } from "./reference-data.js";

export function hydrateInmate(i, trend, ethnicityList, regimeLevelList, cellStatusList, requestDate, numberOfDaysConsideredNewCircumstance) {
    return {
        trendSort: (trend === 'U' ? 1 : (trend === 'D' ? 3 : 2)),
        ethnicityDescription: i.inmateUrn ? getReferenceDataItemFromList(i.ethnicity, ethnicityList, 'description', 'code') : null,
        regimeLevelDescription: getReferenceDataItemFromList(i.regimeLevel, regimeLevelList, 'description', 'level'),
        siteName: getReferenceDataSiteName(i.siteUrn),
        wingCellSort: concatenateStrings([i.unit, i.wing, (i.cell && i.cell.length == 1 ? '0' + i.cell : i.cell), getReferenceDataItemFromList(i.vptCellStatusUrn, cellStatusList, 'ordering', 'vptCellStatusUrn')]),
        cellStatusIcon: getReferenceDataItemFromList(i.vptCellStatusUrn, cellStatusList, 'iconName', 'vptCellStatusUrn'),
        cellStatusDescription: getReferenceDataItemFromList(i.vptCellStatusUrn, cellStatusList, 'description', 'vptCellStatusUrn'),
        cellStatusSort: concatenateStrings([getReferenceDataItemFromList(i.vptCellStatusUrn, cellStatusList, 'ordering', 'vptCellStatusUrn'), i.unit, i.wing, (i.cell && i.cell.length == 1 ? '0' + i.cell : i.cell)]),
        triggerDateSort: i.inmateUrn ? getTriggerDateSort(requestDate, i.triggerDateList) : null,
        daysSinceTemporaryDischarge: i.daysSinceTemporaryDischarge ? i.daysSinceTemporaryDischarge : '0',
        surveyOverdue: i.inmateUrn ? getSurveyOverdueStatus(i.surveyScheduleNextSurveyDate) : null,
        ...getCircumstanceChangeProperties(i, requestDate, numberOfDaysConsideredNewCircumstance),
        contactWarning: getContactWarning(i.contactActionOverdueDays, i.contactIndicatorTargetedTotalIncreased, i.contactIndicatorIsolationIncreased, i.contactTargetedUnseenDays),
        contactOverdue: getContactOverdueOnly(i.contactActionOverdueDays, i.contactIndicatorTargetedTotalIncreased, i.contactIndicatorIsolationIncreased),
        contactSort: getContactSort(i.contactId, i.contactActionOverdueDays, i.contactIndicatorTargetedTotalIncreased, i.contactIndicatorIsolationIncreased, i.contactTargetedUnseenDays)
    }
}

function getCircumstanceChangeProperties(i, requestDate, numberOfDaysConsideredNewCircumstance) {
    // If the number of days considered a new circumstance is not provided, return null
    if (numberOfDaysConsideredNewCircumstance == null) { return null }

    let circumstanceChangeProperties = {};

    if (i.cellStartDate && !(getDaysDifference(getIsoDateStringAsDate(i.cellStartDate), getIsoDateStringAsDate(i.dateArrived)) == 0)) {
        circumstanceChangeProperties.isRecentCellChange = 
            getDaysDifference(getIsoDateStringAsDate(requestDate), getIsoDateStringAsDate(i.cellStartDate)) < numberOfDaysConsideredNewCircumstance;
    }

    if (i.regimeStartDate && !(getDaysDifference(getIsoDateStringAsDate(i.regimeStartDate), getIsoDateStringAsDate(i.dateArrived)) == 0)) {
        circumstanceChangeProperties.isRecentRegimeChange = 
            getDaysDifference(getIsoDateStringAsDate(requestDate), getIsoDateStringAsDate(i.regimeStartDate)) < numberOfDaysConsideredNewCircumstance;
    }

    if (i.keyworkerStartDate && !(getDaysDifference(getIsoDateStringAsDate(i.keyworkerStartDate), getIsoDateStringAsDate(i.dateArrived)) == 0)) {
        circumstanceChangeProperties.isRecentKeyworkerChange = 
            getDaysDifference(getIsoDateStringAsDate(requestDate), getIsoDateStringAsDate(i.keyworkerStartDate)) < numberOfDaysConsideredNewCircumstance;
    }

    if (i.statusStartDate && !(getDaysDifference(getIsoDateStringAsDate(i.statusStartDate), getIsoDateStringAsDate(i.dateArrived)) == 0)) {
        circumstanceChangeProperties.isRecentStatusChange = 
            getDaysDifference(getIsoDateStringAsDate(requestDate), getIsoDateStringAsDate(i.statusStartDate)) < numberOfDaysConsideredNewCircumstance;
    }

    return circumstanceChangeProperties;
}

export function getInmateSurveyModel(i) {
    return {
        inmateUrn: i.inmateUrn,
        inmateName: i.inmateName,
        surveyScheduleUrn: i.surveyScheduleUrn,
        surveyScheduleTotal: i.surveyScheduleTotal,
        surveyScheduleFrequency: i.surveyScheduleFrequency,
        surveyScheduleNextSurveyDate: i.surveyScheduleNextSurveyDate,
        surveyScheduleNumberCompleted: i.surveyScheduleNumberCompleted
    };
}

export function getInmateContactModel(i) {
    return {
        inmateUrn: i.inmateUrn,
        inmateName: i.inmateName,
        contactId : i.contactId,
        contactDateTime : i.contactDateTime,
        previousNotes : i.contactNotes,
        staff : i.contactStaffName,
        dueDate : i.contactActionDueDate,
        previousAction : i.contactActionNotes,
        overdueDays : i.contactActionOverdueDays,
        targetedTotalIncreased : i.contactIndicatorTargetedTotalIncreased,
        isolationIncreased : i.contactIndicatorIsolationIncreased,
    };
}

export function getInmateSearchModel(i) {
    return {
        pn: i.prisonNumber,
        name: i.inmateName,
        dob: formatDate(i.dob),
        nationality: i.nationality,
        religion: i.religion,
        unit: i.unit,
        wing: i.wing,
        cell: i.cell,
        status: i.status,
        keyworker: i.keyworkerName,
        site: i.siteName,
        location: i.wingCellSort,
        regime: i.regimeLevelDescription,
        ethnicity: i.ethnicityDescription 
    };
}

export function getInmateDetailsTooltip(inmate) {
    let tooltipContent = [];

    // Construct the first line of the tooltip with prison number and date of birth
    let firstLine = inmate.prisonNumber.toUpperCase();
    if (inmate.dob) {
        firstLine += " \u00B7 " + formatDate(inmate.dob);
        if (inmate.age) {
            firstLine += " (" + inmate.age + ")";
        }
    }
    tooltipContent.push(firstLine);

    // Construct the second line of the tooltip with nationality and ethnicty
    if (inmate.nationality || inmate.ethnicity) {
        let secondLine;

        if (inmate.nationality && !inmate.ethnicity) {
            secondLine = inmate.nationality;
        } else if (inmate.ethnicity && !inmate.nationality) {
            secondLine = inmate.ethnicityDescription;
        } else {
            secondLine = inmate.nationality + " \u00B7 " + inmate.ethnicityDescription;
        }

        tooltipContent.push(secondLine);
    }

    // Construct the third line of the tooltip with religion, if available
    if (inmate.religion) {
        let thirdLine = inmate.religion;

        if (inmate.religion.toLowerCase() == 'unknown' || inmate.religion.toLowerCase() == 'nil') {
            thirdLine += " Religion";
        }
        tooltipContent.push(thirdLine);
    }

    return {tooltipLines: tooltipContent };
}

export function getArrivalDateAndTimeText(reportDate, dateArrived, hoursSinceArrival) {
    if (isDateInPast(reportDate)) {
        return formatDate(dateArrived);
    } else {
        if (hoursSinceArrival == 0) {
            return "In the last hour";
        } else {
            return `${hoursSinceArrival} hour${hoursSinceArrival == 1 ? '' : 's'} ago`;
        }
    }
}

export function getArrivalDateAndTimeTooltip(inmate) {
    let tooltipContent = [];

    tooltipContent.push("Arrival Date: " + formatDateTime(inmate.dateArrived));

    if (inmate.indicatorNewInmate) {
        tooltipContent.push("Within New Arrival Period");
    }
    else if (!inmate.indicatorNewInmate && inmate.indicatorEarlyDays) {
        tooltipContent.push("Within Early Days Period");
    }

    return {tooltipLines: tooltipContent };
}

export function getTemporaryDischargeTooltip(daysSinceTemporaryDischarge, dischargedTo) {
    let tooltipContent = [];

    if (daysSinceTemporaryDischarge < 2 ) {
        tooltipContent.push("Discharged " + (daysSinceTemporaryDischarge == 0 ? "Today" : "Yesterday"));
    } else {
        tooltipContent.push("Discharged " + daysSinceTemporaryDischarge + " Days Ago");
    } 

    tooltipContent.push("(" + dischargedTo + ")");

    return {tooltipLines: tooltipContent };
}

export function getCircumstanceDateTooltip(startDate, finishDate) {
    if (startDate) {
        if (finishDate) {
            return `${formatDate(startDate)} to ${formatDate(finishDate)}`;
        } else {
            return `Since ${formatDate(startDate)}`;
        }
    }
    return null;
}

export function getArrivalTooltip(dateArrived, isNewInmate, isEarlyDays) {
    if (dateArrived) {
        let arrivalTooltip = `Arrival Date: ${formatDate(dateArrived)}`;
        if (isNewInmate) {
            arrivalTooltip += ' Within New Arrival Period';
        }
        if (!isNewInmate && isEarlyDays) {
            arrivalTooltip += ' Within Early Days Period';
        }
        return arrivalTooltip;
    }
    return null;
}

export const ModalTypeEnum = {
    SURVEY: 'survey',
    ADD_CONTACT: 'add-contact',
    CONTACT: 'contact',
};